/**
 * ServicesList — Catálogo de servicios (estándar de listados del CMDB).
 *
 * QUÉ CAMBIA
 * ---------------------------------------------------------------------------
 * Antes: cabecera y filtros propios (`wol-*`), tabla propia con un `renderCell`
 * a mano, estado de orden propio, un `ViewToggle` de tres vistas, modal de
 * borrado propio y los **grupos** como tarjetas sueltas encima de la tabla
 * (que además se comían el buscador: los grupos no se filtraban).
 *
 * Ahora: `ListPage` con buscador, filtros, columnas, vistas y export; la ficha
 * entra en **lectura** y el borrado usa el diálogo único.
 *
 * LOS GRUPOS NO SE PIERDEN: pasan a ser una **vista** más del listado
 * («Grupos»), donde siguen viviendo las mismas tarjetas desplegables de siempre
 * (`ServiceGroupCard`) con toda su gestión —editar, borrar, añadir y quitar
 * miembros, mapa del grupo—. El alta de grupos está en la cabecera («Nuevo
 * grupo»), como las acciones transversales del resto del CMDB.
 *
 * Botones propios:
 *  · **Abrir la URL** — la del servicio, en otra pestaña.
 *  · **Ver en el Diseñador** — al grafo de ese servicio.
 *  · **Ver sus accesos** — diálogo con los puntos de acceso (tipo, URL, puerto,
 *    protocolo y equipo) y enlace al CI si lo tiene.
 *  · **Ver sus grupos** — diálogo con los grupos a los que pertenece y su rol.
 *  · **Ver en el Mapa de dependencias** — al mapa (que no acepta foco por URL,
 *    así que abre la pantalla; el servicio se busca allí).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  WrenchScrewdriverIcon,
  ListBulletIcon,
  Squares2X2Icon,
  RectangleGroupIcon,
  ViewColumnsIcon,
  ArrowTopRightOnSquareIcon,
  ShareIcon,
  MapIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import ServiceForm from './ServiceForm';
import ServiceGroupCard from './ServiceGroupCard';
import ServiceGroupWizard from './ServiceGroupWizard';
import ServiceKanban from './ServiceKanban';
import { useListView } from '../views/ViewsContext';
import { useAutoOpen } from '../hooks/useAutoOpen';
import { docElementPath } from '../utils/docElements';
import { tagList } from '../utils/tags';
import { getServerFormStyle } from '../utils/serverFormStyle';
import api from '../services/api';

const STATUS_META = {
  running:     { label: 'En marcha',     badge: 'list-badge--on' },
  stopped:     { label: 'Parado',        badge: 'list-badge--off' },
  maintenance: { label: 'Mantenimiento', badge: 'list-badge--warn' },
  degraded:    { label: 'Degradado',     badge: 'list-badge--warn' },
  unknown:     { label: 'Sin datos',     badge: 'list-badge' },
};

const CRITICALITY_META = {
  critical: { label: 'Crítico', badge: 'list-badge--off' },
  high:     { label: 'Alto',    badge: 'list-badge--warn' },
  medium:   { label: 'Medio',   badge: '' },
  low:      { label: 'Bajo',    badge: '' },
};

const EMPTY_SERVICE = {
  name: '', display_name: '', service_type: '', status: 'running',
  criticality: 'medium', hosting_type: '', owner_team: '', url: '', notes: '',
};

export default function ServicesList({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [services, setServices] = useState([]);
  const [groups, setGroups]     = useState([]);
  const [meta, setMeta]         = useState({ service_types: [], statuses: [], criticalities: [], hosting_types: [] });
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');

  const [filterType, setFilterType]           = useState('');
  const [filterStatus, setFilterStatus]       = useState('');
  const [filterCriticality, setFilterCriticality] = useState('');
  const [filterHosting, setFilterHosting]     = useState('');
  const [filterTeam, setFilterTeam]           = useState('');
  const [filterVendor, setFilterVendor]       = useState('');
  const [filterGroup, setFilterGroup]         = useState('');
  const [filterAccess, setFilterAccess]       = useState('');
  const [filterUrl, setFilterUrl]             = useState('');

  const [selected, setSelected]       = useState(null);
  const [initialMode, setInitialMode] = useState('edit');
  const [hlId, setHlId]               = useState(null);
  const [showWizard, setShowWizard]   = useState(false);

  /* Diálogos */
  const [accessOf, setAccessOf]         = useState(null);
  const [accessPoints, setAccessPoints] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [groupsOf, setGroupsOf]         = useState(null);
  const [toDelete, setToDelete]         = useState(null);   // borrado desde el kanban
  const [deleting, setDeleting]         = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const [svcs, m, grps] = await Promise.all([
        api.getServices(companyId),
        api.getServicesMeta(companyId),
        api.getServiceGroups(companyId).catch(() => ({ groups: [] })),
      ]);
      const list = Array.isArray(svcs) ? svcs : (svcs.services || []);
      setServices(list);
      if (m.service_types) setMeta(m);
      setGroups(grps.groups || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  /* ?destacar=<id>[&abrir=1] */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !services.length) return undefined;
    const id = Number(raw);
    const target = services.find(s => s.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { setInitialMode('view'); setSelected(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`svc-row-${id}`) || document.getElementById(`svc-card-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
  }, [searchParams, services, setSearchParams]);

  /* ── ficha ──────────────────────────────────────────────────────────────── */
  const openView   = (s) => { setInitialMode('view');   setSelected(s); };
  const openEdit   = (s) => { setInitialMode('edit');   setSelected(s); };
  const openCreate = ()  => { setInitialMode('create'); setSelected({ ...EMPTY_SERVICE }); };

  /** Abrir por id (lo usan las tarjetas de grupo, que sólo tienen el id). */
  const openEditById = useCallback(async (svcId) => {
    const svc = services.find(s => s.id === svcId);
    if (svc) { openView(svc); return; }
    try {
      const detail = await api.getService(svcId, companyId);
      if (detail?.id) { setInitialMode('view'); setSelected(detail); }
    } catch (e) { setError(e.message); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, companyId]);

  const closeForm = () => { setSelected(null); load(); };
  useAutoOpen(services, openView);

  const handleFormSave = (created) => {
    load();
    // Si se acaba de crear, el formulario pasa a edición con su id (columnas).
    if (created?.id) setSelected(created);
  };

  const handleDelete = async (svc) => {
    try {
      await api.deleteService(svc.id, companyId);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  /* ── accesos ────────────────────────────────────────────────────────────── */
  const openAccess = async (svc) => {
    setAccessOf(svc);
    setAccessPoints([]);
    setAccessLoading(true);
    try {
      const res = await api.getServiceAccessPoints(svc.id, companyId);
      setAccessPoints(res.data || res || []);
    } catch (e) {
      setError(e.message || 'No se pudieron cargar los accesos');
      setAccessOf(null);
    } finally {
      setAccessLoading(false);
    }
  };

  /* ── filtrado ───────────────────────────────────────────────────────────── */
  const groupedIds = useMemo(
    () => new Set(groups.flatMap(g => (g.members || []).map(m => m.service_id))),
    [groups],
  );

  const teamOptions = useMemo(() => {
    const teams = [...new Set(services.map(s => s.owner_team).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, 'es'));
    return teams.map(v => ({ value: v, label: v }));
  }, [services]);

  const vendorOptions = useMemo(() => {
    const vendors = [...new Set(services.map(s => s.vendor).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, 'es'));
    return vendors.map(v => ({ value: v, label: v }));
  }, [services]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return services.filter((s) => {
      if (filterType && s.service_type !== filterType) return false;
      if (filterStatus && s.status !== filterStatus) return false;
      if (filterCriticality && s.criticality !== filterCriticality) return false;
      if (filterHosting && s.hosting_type !== filterHosting) return false;
      if (filterTeam && s.owner_team !== filterTeam) return false;
      if (filterVendor && s.vendor !== filterVendor) return false;
      if (filterGroup === 'with' && !groupedIds.has(s.id)) return false;
      if (filterGroup === 'without' && groupedIds.has(s.id)) return false;
      if (filterAccess === 'with' && !(s.access_point_count > 0)) return false;
      if (filterAccess === 'without' && s.access_point_count > 0) return false;
      if (filterUrl === 'with' && !s.url) return false;
      if (filterUrl === 'without' && s.url) return false;
      if (!term) return true;
      return [
        s.name, s.display_name, s.vendor, s.version, s.url, s.owner_team, s.notes,
        s.service_type_label, s.hosting_type_label,
        tagList(s.tags).join(' '),
        (s.groups || []).map(g => g.group_name).join(' '),
      ].filter(Boolean).join(' ').toLowerCase().includes(term);
    });
  }, [services, search, groupedIds, filterType, filterStatus, filterCriticality,
    filterHosting, filterTeam, filterVendor, filterGroup, filterAccess, filterUrl]);

  /* ── columnas ───────────────────────────────────────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'name',
        label: tr('svc.name', 'Nombre'),
        sortable: true,
        sortValue: (s) => s.display_name || s.name || '',
        render: (s) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600 }}>{s.display_name || s.name}</span>
            {s.url && <span className="list-hint list-code">{s.url}</span>}
          </span>
        ),
      },
      {
        key: 'service_type',
        label: tr('virt.type', 'Tipo'),
        sortable: true,
        sortValue: (s) => s.service_type_label || s.service_type || '',
        render: (s) => (
          <span className="list-badge">{s.service_type_label || s.service_type || dash}</span>
        ),
      },
      {
        key: 'vendor',
        label: tr('lic.vendor', 'Fabricante'),
        sortable: true,
        render: (s) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span>{s.vendor || dash}</span>
            {s.version && <span className="list-hint">{s.version}</span>}
          </span>
        ),
      },
      {
        key: 'status',
        label: tr('ipam.status', 'Estado'),
        sortable: true,
        sortValue: (s) => s.status || 'unknown',
        render: (s) => {
          const meta_ = STATUS_META[s.status] || { label: s.status_label || s.status || '—', badge: '' };
          return <span className={`list-badge ${meta_.badge}`}>{s.status_label || meta_.label}</span>;
        },
      },
      {
        key: 'criticality',
        label: tr('svc.criticality', 'Criticidad'),
        sortable: true,
        sortValue: (s) => s.criticality || '',
        render: (s) => {
          const meta_ = CRITICALITY_META[s.criticality];
          if (!meta_) return dash;
          return (
            <span className={`list-badge ${meta_.badge}`}>
              {s.criticality_label || meta_.label}
            </span>
          );
        },
      },
      {
        key: 'hosting_type',
        label: tr('svc.hosting', 'Hosting'),
        sortable: true,
        sortValue: (s) => s.hosting_type_label || s.hosting_type || '',
        render: (s) => (s.hosting_type
          ? <span className="list-badge">{s.hosting_type_label || s.hosting_type}</span>
          : dash),
      },
      {
        key: 'access_point_count',
        label: tr('svc.access', 'Accesos'),
        sortable: true,
        sortValue: (s) => s.access_point_count ?? 0,
        render: (s) => (
          <button
            type="button"
            className="list-badge"
            style={{ cursor: s.access_point_count ? 'pointer' : 'default' }}
            disabled={!s.access_point_count}
            onClick={(event) => { if (!s.access_point_count) return; event.stopPropagation(); openAccess(s); }}
            title={tr('svc.view_access', 'Ver sus accesos')}
          >
            {s.access_point_count || 0}
          </button>
        ),
      },
      {
        key: 'groups',
        label: tr('svc.groups', 'Grupos'),
        sortable: true,
        sortValue: (s) => (s.groups || []).length,
        render: (s) => {
          const list = s.groups || [];
          if (!list.length) return <span className="list-hint">{tr('svc.no_group', 'Sin grupo')}</span>;
          return (
            <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
              {list.slice(0, 3).map(g => (
                <span key={g.group_id} className="list-badge" title={g.role ? `${g.group_name} · ${g.role}` : g.group_name}>
                  {g.group_name}
                </span>
              ))}
              {list.length > 3 && <span className="list-hint">+{list.length - 3}</span>}
            </span>
          );
        },
      },
      {
        key: 'owner_team',
        label: tr('svc.team', 'Equipo'),
        sortable: true,
        render: (s) => s.owner_team || dash,
      },
      {
        key: 'tags',
        label: tr('virt.tags', 'Etiquetas'),
        render: (s) => {
          const tags = tagList(s.tags);
          if (!tags.length) return dash;
          return (
            <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
              {tags.slice(0, 3).map(tag => <span key={tag} className="list-badge">{tag}</span>)}
              {tags.length > 3 && <span className="list-hint">+{tags.length - 3}</span>}
            </span>
          );
        },
      },
      {
        key: 'notes',
        label: tr('description', 'Notas'),
        render: (s) => s.notes || dash,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr]);

  const defaultVisible = ['name', 'service_type', 'vendor', 'status', 'criticality', 'access_point_count', 'groups'];

  const filters = [
    {
      key: 'type', label: tr('virt.type', 'Tipo'), allLabel: tr('virt.type_all', 'Todos los tipos'),
      value: filterType, onChange: setFilterType,
      options: (meta.service_types || []).map(x => ({ value: x.value, label: x.label })),
    },
    {
      key: 'status', label: tr('ipam.status', 'Estado'), allLabel: tr('ipam.status_all', 'Todos los estados'),
      value: filterStatus, onChange: setFilterStatus,
      options: (meta.statuses || []).map(x => ({ value: x.value, label: x.label })),
    },
    {
      key: 'criticality', label: tr('svc.criticality', 'Criticidad'), allLabel: tr('svc.criticality_all', 'Todas las criticidades'),
      value: filterCriticality, onChange: setFilterCriticality,
      options: (meta.criticalities || []).map(x => ({ value: x.value, label: x.label })),
    },
    {
      key: 'hosting', label: tr('svc.hosting', 'Hosting'), allLabel: tr('svc.hosting_all', 'Cualquier hosting'),
      value: filterHosting, onChange: setFilterHosting,
      options: (meta.hosting_types || []).map(x => ({ value: x.value, label: x.label })),
    },
    {
      key: 'team', label: tr('svc.team', 'Equipo'), allLabel: tr('svc.team_all', 'Cualquier equipo'),
      value: filterTeam, onChange: setFilterTeam, options: teamOptions,
    },
    {
      key: 'vendor', label: tr('lic.vendor', 'Fabricante'), allLabel: tr('lic.vendor_all', 'Cualquier fabricante'),
      value: filterVendor, onChange: setFilterVendor, options: vendorOptions,
    },
    {
      key: 'group', label: tr('svc.groups', 'Grupo'), allLabel: tr('svc.group_all', 'Con o sin grupo'),
      value: filterGroup, onChange: setFilterGroup,
      options: [
        { value: 'with', label: tr('svc.group_with', 'En un grupo') },
        { value: 'without', label: tr('svc.group_without', 'Sin grupo') },
      ],
    },
    {
      key: 'access', label: tr('svc.access', 'Accesos'), allLabel: tr('svc.access_all', 'Con o sin accesos'),
      value: filterAccess, onChange: setFilterAccess,
      options: [
        { value: 'with', label: tr('svc.access_with', 'Con puntos de acceso') },
        { value: 'without', label: tr('svc.access_without', 'Sin puntos de acceso') },
      ],
    },
    {
      key: 'url', label: 'URL', allLabel: tr('svc.url_all', 'Con o sin URL'),
      value: filterUrl, onChange: setFilterUrl,
      options: [
        { value: 'with', label: tr('svc.url_with', 'Con URL') },
        { value: 'without', label: tr('svc.url_without', 'Sin URL') },
      ],
    },
  ];

  const subtitle = `${services.length} ${tr('svc.plural', 'servicios')}`
    + (groups.length > 0 ? ` · ${groups.length} ${tr('svc.groups_plural', 'grupos')}` : '')
    + ` · ${filtered.length} ${tr('location.filtered', 'filtrados')}`;

  const exportRows = useMemo(() => filtered.map(s => ({
    'Nombre': s.display_name || s.name || '',
    'Nombre interno': s.name || '',
    'URL': s.url || '',
    'Tipo': s.service_type_label || s.service_type || '',
    'Fabricante': s.vendor || '',
    'Versión': s.version || '',
    'Estado': s.status_label || (STATUS_META[s.status] || {}).label || '',
    'Criticidad': s.criticality_label || (CRITICALITY_META[s.criticality] || {}).label || '',
    'Hosting': s.hosting_type_label || s.hosting_type || '',
    'Accesos': s.access_point_count ?? 0,
    'Grupos': (s.groups || []).map(g => (g.role ? `${g.group_name} (${g.role})` : g.group_name)).join(', '),
    'Equipo': s.owner_team || '',
    'Etiquetas': tagList(s.tags).join(', '),
    'Notas': s.notes || '',
  })), [filtered]);

  /** Vista Fichas: las tarjetas del estándar, con los botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((s) => {
          const meta_ = STATUS_META[s.status] || { label: s.status || '—', badge: '' };
          return (
            <div
              key={s.id}
              id={`svc-card-${s.id}`}
              className={`list-card${s.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openView(s)}
              onKeyDown={(event) => { if (event.key === 'Enter') openView(s); }}
              title={`${tr('action.view', 'Ver')} ${s.display_name || s.name}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title">{s.display_name || s.name}</div>
                  {s.url && <span className="list-code">{s.url}</span>}
                </div>
                <span className={`list-badge ${meta_.badge}`}>{s.status_label || meta_.label}</span>
              </div>

              <div className="list-card-meta">
                <span className="list-badge">{s.service_type_label || s.service_type || '—'}</span>
                {s.vendor && <span>{s.vendor}{s.version ? ` ${s.version}` : ''}</span>}
                {s.criticality_label || CRITICALITY_META[s.criticality] ? (
                  <span className="list-hint">
                    {tr('svc.criticality', 'Criticidad')}: {s.criticality_label || CRITICALITY_META[s.criticality].label}
                  </span>
                ) : null}
                <span className="list-badge">{s.access_point_count || 0} {tr('svc.access', 'accesos')}</span>
                {(s.groups || []).map(g => (
                  <span key={g.group_id} className="list-badge">{g.group_name}</span>
                ))}
              </div>

              <div className="list-card-actions">{rowActions(s)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  /** Vista Grupos: las tarjetas de grupo de siempre, con toda su gestión. */
  function renderGroups() {
    if (groups.length === 0) {
      return (
        <div className="list-empty">
          <span aria-hidden="true">🧩</span>
          <p className="empty-title">{tr('svc.no_groups', 'Todavía no hay grupos de servicios')}</p>
          <p className="empty-subtitle">
            {tr('svc.no_groups_hint', 'Un grupo reúne los servicios que forman una aplicación (por ejemplo, el ERP con su base de datos y su web).')}
          </p>
          <button type="button" className="list-btn-primary" onClick={() => setShowWizard(true)}>
            ＋ {tr('svc.new_group', 'Nuevo grupo')}
          </button>
        </div>
      );
    }
    return (
      <div>
        {groups.map(group => (
          <ServiceGroupCard
            key={group.id}
            group={group}
            companyId={companyId}
            onEditService={openEditById}
            onGroupChange={load}
          />
        ))}
      </div>
    );
  }

  /** Vista Kanban: el tablero por estado de siempre. */
  function renderKanban({ rows }) {
    return (
      <ServiceKanban
        services={rows}
        onEdit={openView}
        onDelete={(svc) => setToDelete(svc)}
      />
    );
  }

  const SVC_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ipam.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ipam.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ipam.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ipam.view_cards_hint', 'Tarjetas con lo esencial de cada servicio'), render: renderCards },
    { key: 'groups', label: tr('svc.groups', 'Grupos'), icon: RectangleGroupIcon, title: tr('svc.groups_hint', 'Los grupos de servicios y sus miembros'), render: renderGroups },
    { key: 'kanban', label: 'Kanban', icon: ViewColumnsIcon, title: tr('svc.kanban_hint', 'Tablero por estado'), render: renderKanban },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId, groups, companyId]);
  useListView('services', 'list');

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1.5rem' }}>
        <div className="alert alert-warning">
          {tr('svc.select_company', 'Selecciona una empresa para ver el catálogo de servicios.')}
        </div>
      </div>
    );
  }

  const confirmKanbanDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.deleteService(toDelete.id, companyId);
      setToDelete(null);
      await load();
    } catch (e) {
      setError(e.message);
      setToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      {showWizard && (
        <ServiceGroupWizard
          companyId={companyId}
          onClose={() => setShowWizard(false)}
          onCreated={() => { setShowWizard(false); load(); }}
        />
      )}

      {selected !== null && (
        <ServiceForm
          service={selected && selected.id ? selected : null}
          companyId={companyId}
          meta={meta}
          initialMode={initialMode}
          variant={getServerFormStyle() === 'cards' ? 'cards' : 'classic'}
          onClose={closeForm}
          onSave={handleFormSave}
          onOpenService={(id) => openView(services.find(s => s.id === id) || { id })}
        />
      )}

      <div style={{ display: selected !== null ? 'none' : undefined }}>
        <ListPage
          icon={WrenchScrewdriverIcon}
          title={tr('svc.title', 'Catálogo de servicios')}
          subtitle={subtitle}

          onCreate={openCreate}
          createLabel={tr('svc.new', 'Nuevo servicio')}
          createTitle={tr('svc.new_hint', 'Servicio del catálogo (aplicación, plataforma, servicio gestionado…)')}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('svc.search', 'Buscar por nombre, fabricante, URL, equipo, etiqueta…')}
          filters={filters}

          exportRows={exportRows}
          exportFileBase="servicios"
          exportSheetName="Catálogo de servicios"

          columns={columns}
          columnStorageKey="cmdb_cols_services"
          companyId={companyId}
          importEntity="services"
          onImported={() => load()}
          defaultVisible={defaultVisible}
          rows={filtered}
          rowKey={(s) => s.id}
          rowId={(s) => `svc-row-${s.id}`}
          rowClassName={(s) => (s.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={openView}
          rowTitle={(s) => `${tr('action.view', 'Ver')} ${s.display_name || s.name}`}

          headerActions={(
            <button
              type="button"
              className="list-btn-secondary"
              onClick={() => setShowWizard(true)}
              title={tr('svc.new_group_hint', 'Crear un grupo de servicios con el asistente')}
            >
              <RectangleGroupIcon style={{ width: 15, height: 15 }} /> {tr('svc.new_group', 'Nuevo grupo')}
            </button>
          )}

          views={SVC_VIEWS}
          listKey="services"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando servicios…')}
          error={error}

          empty={{
            icon: '⚙️',
            title: services.length === 0
              ? tr('svc.empty', 'No hay servicios todavía')
              : tr('msg.no_results', 'Ningún servicio coincide con los filtros'),
            subtitle: services.length === 0
              ? tr('svc.empty_hint', 'Crea el primero para empezar a catalogar tus servicios.')
              : undefined,
            action: services.length === 0 ? (
              <button type="button" className="list-btn-primary" onClick={openCreate}>
                ＋ {tr('svc.new', 'Nuevo servicio')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (s) => (
              <>
                {/* Abrir la URL */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!s.url}
                  onClick={() => window.open(s.url, '_blank', 'noopener')}
                  title={s.url
                    ? `${tr('svc.open_url', 'Abrir la URL del servicio')}: ${s.url}`
                    : tr('svc.no_url', 'Sin URL')}
                >
                  <ArrowTopRightOnSquareIcon />
                </button>
                {/* Ver en el Diseñador */}
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => navigate(`/services/designer?service=${s.id}`)}
                  title={tr('virt.view_in_designer', 'Ver en el Diseñador de servicios')}
                >
                  <ShareIcon />
                </button>
                {/* Ver sus accesos */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!(s.access_point_count > 0)}
                  onClick={() => openAccess(s)}
                  title={tr('svc.view_access', 'Ver sus accesos')}
                >
                  <KeyIcon />
                </button>
                {/* Ver sus grupos */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!(s.groups || []).length}
                  onClick={() => setGroupsOf(s)}
                  title={tr('svc.view_groups', 'Ver sus grupos')}
                >
                  <RectangleGroupIcon />
                </button>
                {/* Ver en el Mapa de dependencias */}
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => navigate('/services/dependency-map')}
                  title={tr('svc.view_depmap', 'Ver en el Mapa de dependencias')}
                >
                  <MapIcon />
                </button>
              </>
            ),
            onEdit: openEdit,
            onDelete: handleDelete,
            deleteTitle: tr('svc.delete_title', 'Eliminar servicio'),
            deleteMessage: (s) => (
              <>
                {tr('virt.delete_confirm', '¿Seguro que quieres eliminar')}{' '}
                <strong>{s.display_name || s.name}</strong>?{' '}
                {tr('svc.delete_used', 'Se quitarán también sus accesos, sus CIs vinculados y sus dependencias.')}
              </>
            ),
          }}
        />
      </div>

      {/* ══ Diálogo: accesos del servicio ══ */}
      {accessOf && (
        <Dialog
          title={`${tr('svc.access_of', 'Accesos de')} ${accessOf.display_name || accessOf.name}`}
          icon={<KeyIcon style={{ width: 18, height: 18 }} />}
          maxWidth={760}
          onClose={() => setAccessOf(null)}
          footer={(
            <button type="button" className="list-btn-secondary" onClick={() => setAccessOf(null)}>
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          {accessLoading && <p style={{ margin: 0 }}>{tr('msg.loading', 'Cargando…')}</p>}
          {!accessLoading && accessPoints.length === 0 && (
            <p className="list-hint" style={{ margin: 0 }}>
              {tr('svc.no_access', 'Este servicio no tiene puntos de acceso declarados.')}
            </p>
          )}
          {!accessLoading && accessPoints.length > 0 && (
            <div>
              {accessPoints.map(ap => {
                const path = ap.ci_id && ap.ci_type ? docElementPath(ap.ci_type, ap.ci_id) : null;
                return (
                  <div
                    key={ap.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                      padding: '6px 0', borderBottom: '1px solid var(--border)',
                    }}
                  >
                    <span style={{ fontWeight: 600, minWidth: 140 }}>{ap.name || ap.access_type_label || ap.access_type}</span>
                    <span className="list-badge">{ap.access_type_label || ap.access_type || '—'}</span>
                    {ap.protocol && <span className="list-code">{ap.protocol}{ap.port ? `:${ap.port}` : ''}</span>}
                    {ap.ci_type_label && (
                      path
                        ? (
                          <button
                            type="button"
                            className="list-badge"
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigate(path)}
                            title={tr('svc.view_ci', 'Ver el equipo')}
                          >
                            {ap.ci_type_label}{ap.ci_id ? ` #${ap.ci_id}` : ''}
                          </button>
                        )
                        : <span className="list-badge">{ap.ci_type_label}</span>
                    )}
                    {ap.url && (
                      <a href={ap.url} target="_blank" rel="noopener noreferrer" className="list-code" style={{ marginLeft: 'auto' }}>
                        {ap.url}
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Dialog>
      )}

      {/* ══ Diálogo: grupos del servicio ══ */}
      {groupsOf && (
        <Dialog
          title={`${tr('svc.groups_of', 'Grupos de')} ${groupsOf.display_name || groupsOf.name}`}
          icon={<RectangleGroupIcon style={{ width: 18, height: 18 }} />}
          maxWidth={560}
          onClose={() => setGroupsOf(null)}
          footer={(
            <button type="button" className="list-btn-secondary" onClick={() => setGroupsOf(null)}>
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          {(groupsOf.groups || []).length === 0 && (
            <p className="list-hint" style={{ margin: 0 }}>
              {tr('svc.no_group', 'Sin grupo')}
            </p>
          )}
          {(groupsOf.groups || []).map(g => (
            <div
              key={g.group_id}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '6px 0', borderBottom: '1px solid var(--border)',
              }}
            >
              <span style={{ fontWeight: 600 }}>{g.group_name}</span>
              {g.role && <span className="list-badge">{g.role}</span>}
            </div>
          ))}
          <p className="list-hint" style={{ marginTop: 10, marginBottom: 0 }}>
            {tr('svc.groups_hint_manage', 'Los grupos se gestionan desde la vista «Grupos» del listado.')}
          </p>
        </Dialog>
      )}

      {/* ══ Borrado desde el kanban (el de la tabla lo pone ListPage) ══ */}
      {toDelete && (
        <Dialog
          title={tr('svc.delete_title', 'Eliminar servicio')}
          icon={<WrenchScrewdriverIcon style={{ width: 18, height: 18 }} />}
          maxWidth={460}
          onClose={() => setToDelete(null)}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setToDelete(null)} disabled={deleting}>
                {tr('action.cancel', 'Cancelar')}
              </button>
              <button type="button" className="list-btn-danger" onClick={confirmKanbanDelete} disabled={deleting}>
                {tr('action.delete', 'Eliminar')}
              </button>
            </>
          )}
        >
          <p style={{ margin: 0 }}>
            {tr('virt.delete_confirm', '¿Seguro que quieres eliminar')}{' '}
            <strong>{toDelete.display_name || toDelete.name}</strong>?
            {' '}{tr('svc.delete_used', 'Se quitarán también sus accesos, sus CIs vinculados y sus dependencias.')}
          </p>
        </Dialog>
      )}
    </>
  );
}
